<template>
  <div class="ibox">
    <div class="ibox-content">
      <h1 class="p-t-m m-t-none font-heavy">Select Divisions</h1>
      <p class="text-gray-600 m-b-md">
        Choose an age division and weight class for each wrestler.
      </p>

      <!-- Missing data warning -->
      <div
        v-if="wrestlersWithMissingData.length > 0"
        class="alert alert-warning m-b-md"
      >
        <p class="m-b-xs font-semi">
          <i class="fa-solid fa-triangle-exclamation m-r-xs"></i>
          Missing Information
        </p>
        <p class="m-b-xs">
          The following wrestlers need updated profiles before selecting a
          division:
        </p>
        <ul class="m-b-none">
          <li v-for="wrestler in wrestlersWithMissingData" :key="wrestler.id">
            <strong>{{ wrestler.full_name }}</strong> - missing
            {{ getMissingFieldsText(wrestler) }}
          </li>
        </ul>
        <p class="m-t-sm m-b-none">
          <a
            href="/new-wrestler-wizard?override_funnel=true"
            class="text-underline"
          >
            Go back to update wrestler information
          </a>
        </p>
      </div>

      <!-- Division selection cards for each wrestler -->
      <div v-if="wrestlersWithMissingData.length === 0">
        <div
          v-for="wrestler in wrestlers"
          :key="wrestler.id"
          class="border border-solid border-gray-200 rounded-xs p-md m-b-md"
        >
          <wrestler-division-card
            :wrestler="wrestler"
            :enabled-divisions="enabledDivisions"
            :competition-year="competitionYear"
            :selected-age-division-id="getSelectedAgeDivisionId(wrestler.id)"
            :selected-weight-class-id="getSelectedWeightClassId(wrestler.id)"
            :brand-color="brandColor"
            @update:selected-age-division-id="
              (val) => updateAgeDivision(wrestler.id, val)
            "
            @update:selected-weight-class-id="
              (val) => updateWeightClass(wrestler.id, val)
            "
          />
          <hr/>
          <div
            v-if="true || canRemoveWrestlers"
            class="flex m-t-md"
          >
            <button
              class="btn btn-xs btn-danger btn-outline"
              @click="removeWrestler(wrestler)"
            >
              <i class="fa-solid fa-xmark m-r-xxs text-red-500"></i>
              Remove {{ wrestler.full_name }} from registration
            </button>
          </div>
        </div>
      </div>

      <!-- Validation message -->
      <div
        v-if="!allSelectionsComplete && wrestlersWithMissingData.length === 0"
        class="alert alert-info m-t-md"
      >
        <i class="fa-solid fa-circle-info m-r-xs"></i>
        Please select a division and weight class for each wrestler to continue.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert2';
import WrestlerDivisionCard from './wrestler_division_card.vue';
import {
  getEnabledDivisions,
  isEligibleForDivision,
  validateWrestlerForDivisions,
} from '../utils/divisionEligibility.js';

export default {
  name: 'division-selection-step',
  components: {
    WrestlerDivisionCard,
  },
  props: {
    wrestlers: {
      type: Array,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    wrestlerDivisions: {
      type: Object,
      required: true,
    },
    brandColor: {
      type: String,
      default: '#27AA83',
    },
  },
  computed: {
    enabledDivisions() {
      return getEnabledDivisions(this.session);
    },
    competitionYear() {
      // Use session's competition year if available, otherwise current year
      if (this.session.start_at) {
        return new Date(this.session.start_at).getFullYear();
      }
      return new Date().getFullYear();
    },
    wrestlersWithMissingData() {
      return this.wrestlers.filter((wrestler) => {
        const validation = validateWrestlerForDivisions(wrestler);
        return !validation.valid;
      });
    },
    allSelectionsComplete() {
      return this.wrestlers.every((wrestler) => {
        const selection = this.wrestlerDivisions[wrestler.id];
        return (
          selection?.selectedAgeDivisionId && selection?.selectedWeightClassId
        );
      });
    },
    canRemoveWrestlers() {
      return this.wrestlers.length > 1;
    },
  },
  methods: {
    getMissingFieldsText(wrestler) {
      const validation = validateWrestlerForDivisions(wrestler);
      return validation.missingFields.join(', ');
    },
    getSelectedAgeDivisionId(wrestlerId) {
      return this.wrestlerDivisions[wrestlerId]?.selectedAgeDivisionId || null;
    },
    getSelectedWeightClassId(wrestlerId) {
      return this.wrestlerDivisions[wrestlerId]?.selectedWeightClassId || null;
    },
    updateAgeDivision(wrestlerId, divisionId) {
      const updated = {
        ...this.wrestlerDivisions,
        [wrestlerId]: {
          ...this.wrestlerDivisions[wrestlerId],
          selectedAgeDivisionId: divisionId,
          selectedWeightClassId: null, // Reset weight class when division changes
        },
      };
      this.$emit('update:wrestler-divisions', updated);
    },
    updateWeightClass(wrestlerId, weightClassId) {
      const updated = {
        ...this.wrestlerDivisions,
        [wrestlerId]: {
          ...this.wrestlerDivisions[wrestlerId],
          selectedWeightClassId: weightClassId,
        },
      };
      this.$emit('update:wrestler-divisions', updated);
    },
    removeWrestler(wrestler) {
      swal.fire({
        icon: 'warning',
        title: `Remove ${wrestler.full_name}?`,
        text: 'They will be removed from this registration. You can add them back by starting over.',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          const url = this.$apiService.funnelRemoveWrestlerUrl(wrestler.id);
          axios.delete(url).then(() => {
            window.location.reload();
          }).catch((error) => {
            const errors = error.response?.data?.errors || [];
            swal.fire({
              icon: 'error',
              title: 'Unable to remove',
              text: errors.join(', ') || 'Something went wrong.',
            });
          });
        }
      });
    },
  },
};
</script>
